import React from 'react';
import { Table, Modal, Descriptions, Button, Image, Tag, Spin, message } from 'antd';
import moment from 'moment';

// enums / maps to show friendly labels in the UI
const WORK_ORDER_TYPE_MAP = {
    EM: 'Emergency Maintenance',
    PM: 'Preventive Maintenance',
    CM: 'Corrective Maintenance',
};

const ASSET_TYPE_MAP = {
    COMPUTER: 'Computer',
    PRINTER: 'Printer',
    SCANNER: 'Scanner',
    ATM_MACHINE: 'ATM Machine',
    CASH_COUNTING_MACHINE: 'Cash Counting Machine',
    CCTV_CAMERA: 'CCTV Camera',
    AIR_CONDITIONER: 'Air Conditioner',
    UPS: 'UPS',
    FURNITURE: 'Furniture',
    VEHICLE: 'Vehicle',
    SAFE_LOCKER: 'Safe Locker',
    NETWORK_SWITCH: 'Network Switch',
    GENERATOR: 'Generator',
    FIRE_EXTINGUISHER: 'Fire Extinguisher',
    OTHER: 'Other',
};

const STATUS_MAP = {
    INITIATED: 'Initiated',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    ON_HOLD: 'On Hold',
};

const statusColor = (statusKey) => {
    if (!statusKey) return 'blue';
    switch ((statusKey || '').toString().toUpperCase()) {
        case 'COMPLETED':
            return 'green';
        case 'IN_PROGRESS':
            return 'orange';
        case 'CANCELLED':
            return 'grey';
        case 'INITIATED':
            return 'volcano';
        default:
            return 'blue';
    }
};

const mapDtoToRow = (dto) => ({
    id: dto?.id,
    branchID: dto?.branchID ?? dto?.branchId ?? null,
    workorderType: WORK_ORDER_TYPE_MAP[dto?.workorderType] ?? dto?.workorderType ?? null,
    assetType: ASSET_TYPE_MAP[dto?.assetType] ?? dto?.assetType ?? null,
    assetID: dto?.assetID ?? dto?.assetId ?? null,
    remark: dto?.remark ?? null,
    imageURLs: Array.isArray(dto?.imageURLs) ? dto.imageURLs : dto?.imageUrls ?? [],
    statusKey: dto?.status ?? (dto?.status?.id ? dto.status.id : null),
    status: STATUS_MAP[dto?.status] ?? dto?.status ?? dto?.status?.name ?? null,
    createdOn: dto?.createdOn ?? dto?.createdOnString ?? null,
    createdBy: dto?.createdBy ?? null,
    lastUpdatedOn: dto?.lastUpdatedOn ?? dto?.lastUpdatedOnString ?? null,
});

const AssetsChecklists = () => {
    const [checklists, setChecklists] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedChecklist, setSelectedChecklist] = React.useState(null);
    const [modalVisible, setModalVisible] = React.useState(false);

    let ImageBaseURL = "https://storage.googleapis.com/dns_storage_v2/datacollection/"

    React.useEffect(() => {
        fetchChecklists();
    }, []);

    const fetchChecklists = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://dnsuatapi.eimsky.com/dns-workorder-app/api/v1/workorders', {
                method: 'GET',
                headers: {
                    "cache-control": "no-cache",
                    "Content-Type": "application/json"
                }
            });
            // parse response and map DTOs to table rows
            const json = await response.json().catch(() => null);
            const list = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
            const rows = list.map(mapDtoToRow);
            setChecklists(rows);
        } catch (error) {
            console.error("Error fetching checklists:", error);
            message.error("Failed to load checklists.");
        }
        setLoading(false);
    };

    const columns = [
        { title: 'Branch Name', dataIndex: 'branchID', key: 'branchID' },
        { title: 'Workorder Type', dataIndex: 'workorderType', key: 'workorderType' },
        { title: 'Asset Type', dataIndex: 'assetType', key: 'assetType' },
        { title: 'Asset ID', dataIndex: 'assetID', key: 'assetID' },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
            render: (text) => {
                let color = text === 'COMPLETED' ? 'green' : text === 'IN_PROGRESS' ? 'blue' : 'orange';
                return <Tag color={color}>{text}</Tag>;
            }
        },
        {
            title: 'Created On', dataIndex: 'createdOn', key: 'createdOn',
            render: (text) => text ? moment(text).format('DD-MM-YYYY HH:mm') : 'N/A'
        },
        { title: 'Created By', dataIndex: 'createdBy', key: 'createdBy' },
    ];

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-sm-12">
                    <div className="page-title-box">
                        <div className="row">
                            <div className="col">
                                <h4 className="page-title">
                                    <span className="textStyles-small" style={{ fontSize: 16 }}>All Workorders</span>
                                </h4>
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <a href="javascript:void(0);">
                                            <span className="textStyles-small" style={{ fontSize: 12 }}>Branch Inspections</span>
                                        </a>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <a href="javascript:void(0);">
                                            <span className="textStyles-small" style={{ fontSize: 12 }}>All Inspections</span>
                                        </a>
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Spin spinning={loading}>
                <Table
                    dataSource={checklists}
                    columns={columns}
                    rowKey="id"
                    pagination={checklists?.length > 10 ? { pageSize: 10 } : false}
                    className="textStyles-small table-striped-rows"
                    onRow={(record) => ({
                        onClick: () => {
                            setSelectedChecklist(record);
                            setModalVisible(true);
                        }
                    })}
                    rowClassName={() => 'cursor-pointer'}
                />
            </Spin>

            <Modal
                title={<span className='textStyles-small' style={{fontSize: 14, fontWeight: 500}}>Checklist Details</span>}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                width={700}
                footer={null}
            >
                {selectedChecklist && (
                    <Descriptions bordered column={2}>
                        <Descriptions.Item className='textStyles-small' label="ID">CDB-{selectedChecklist.id}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Branch Name">{selectedChecklist.branchID}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Workorder Type">{selectedChecklist.workorderType}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Asset Type">{selectedChecklist.assetType}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Asset ID">{selectedChecklist.assetID}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Status">{selectedChecklist.status}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Created On">{moment(selectedChecklist.createdOn).format('DD-MM-YYYY HH:mm')}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Created By">{selectedChecklist.createdBy}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Last Updated On">{moment(selectedChecklist.lastUpdatedOn).format('DD-MM-YYYY HH:mm')}</Descriptions.Item>
                        <Descriptions.Item className='textStyles-small' label="Images">
                            {selectedChecklist.imageURLs && selectedChecklist.imageURLs.length > 0 ? (
                                selectedChecklist.imageURLs.map((url, index) => (
                                    <Image key={index} src={ImageBaseURL + url} width={50} height={50} style={{ padding: 10, borderRadius: 5 }} />
                                ))
                            ) : (
                                'No images available.'
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </div>
    );
};

export default AssetsChecklists;